/**
 * 单词水果忍者 mixin（单人模式）
 * 玩法（参考 edu.tksai.com/preview/32）：
 * - 水果从底部抛出（抛物线轨迹），点击切割
 * - 目标词中文显示在顶部卡片，水果上写英文单词
 * - 切中目标词水果：+1 分，3 连击起每刀额外 +1 分（连击奖励）
 * - 切错（非目标词）：扣 1 条生命（共 3 条），连击清零
 * - 水果落出屏幕不扣分；每轮抛出 4 个水果（1 目标 + 3 干扰）
 * - 完成全部目标词、生命耗尽或时间到即结束，结算并记入排行榜
 * - 排行榜仅保存在内存中：关闭页面再打开自动清空
 */
import gameAudioManager from "@/utils/gameAudioManager";

const NINJA_GRAVITY = 0.11; // 重力（px/帧²，60fps 基准）——再次调低：停留更久、消失更慢
const NINJA_SPAWN_OFFSET = 100; // 水果跃出位置：场地底部下方 100px
const NINJA_ROUND_SIZE = 4; // 每轮水果数（1 目标 + 3 干扰）
const NINJA_FRUIT_COLORS = ["#ff6b6b", "#ff9f43", "#f9ca24", "#6ab04c", "#a55eea", "#54a0ff"];

/** 点到线段的最近距离（滑动切割碰撞检测：防止快速滑动时采样点跳过水果） */
function ninjaSegmentDistance(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (!lenSq) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

function createNinjaPlayer() {
  return {
    lives: 3,
    combo: 0,
    maxCombo: 0,
    correct: 0,
    wrong: 0,
    targetList: [],
    targetIndex: 0,
    fruits: [],
    particles: [],
    slashes: [],
    comboFlash: null,
    failFlash: false,
    nextSpawnAt: 0,
    completed: false,
    swipeLast: null, // 滑动切割：上一采样点（相对场地坐标）
  };
}

export default {
  data() {
    return {
      ninjaData: {
        player1: createNinjaPlayer(),
      },
      ninjaRunning: false,
      // 忍者榜：sessionStorage 会话级存储——刷新页面保留，关闭页面（标签页）自动清空
      ninjaRankList: (() => {
        try {
          return JSON.parse(sessionStorage.getItem("ninja_rank_list")) || [];
        } catch (err) {
          return [];
        }
      })(),
      ninjaRankRecorded: false,
      ninjaResultVisible: false, // 本局成绩弹窗显示状态（endGame 后自动弹出）
      ninjaGameResult: null, // 本局胜负：win=切完全部目标词 / lose=生命耗尽
      ninjaStartTime: 0, // 本局开始时间戳（用于统计用时）
      ninjaLastDuration: 0, // 本局用时（秒，endGame 时结算）
      ninjaMouseDragging: false, // 鼠标拖动切割标记（PC 端：按住拖动才触发，避免移动即切割）
    };
  },
  computed: {
    /** 本局正确率（切中 / 总切割次数） */
    ninjaAccuracy() {
      const p = this.ninjaData.player1;
      const total = p.correct + p.wrong;
      return total ? Math.round((p.correct / total) * 100) : 0;
    },
  },
  methods: {
    /** 初始化水果忍者（倒计时结束后由 downStartGame 分支调用） */
    initNinjaGame() {
      const vocab = this.vocabulary || [];
      if (!vocab.length) {
        this.gameState = "setup";
        return;
      }
      // 先停掉上一局可能残留的 rAF 循环，避免多循环并发导致水果加速
      this.ninjaStopLoop();
      const count = Math.max(1, Math.min(this.questionCount || vocab.length, vocab.length));
      const shuffled = [...vocab].sort(() => 0.5 - Math.random()).slice(0, count);
      const p = createNinjaPlayer();
      p.targetList = shuffled;
      this.ninjaData = { player1: p };
      this.ninjaRankRecorded = false;
      this.ninjaResultVisible = false;
      this.ninjaGameResult = null;
      this.ninjaStartTime = Date.now();
      this.ninjaMouseDragging = false;
      this.gameState = "playing";
      this.optionsPanelVisible = false;
      this.ninjaRunning = true;
      this.$nextTick(() => {
        this.ninjaData.player1.nextSpawnAt = performance.now() + 300;
        this.ninjaLoop(performance.now());
      });
    },

    /** 获取游戏场地 DOM */
    ninjaGetField() {
      let field = this.$refs.ninjaField || this.$refs["ninjaField-player1"];
      if (Array.isArray(field)) field = field[0];
      return field;
    },

    /** rAF 主循环：水果抛物线物理 + 轮次调度 */
    ninjaLoop(now) {
      if (!this.ninjaRunning) return;
      if (this.ninjaLastTime == null) this.ninjaLastTime = now;
      const dt = Math.min(3, Math.max(0.5, (now - this.ninjaLastTime) / 16.67));
      this.ninjaLastTime = now;
      this.ninjaStepPlayer("player1", now, dt);
      this.ninjaRafId = requestAnimationFrame(this.ninjaLoop);
    },

    /** 物理推进与轮次调度 */
    ninjaStepPlayer(key, now, dt) {
      const p = this.ninjaData[key];
      p.fruits.forEach((f) => {
        f.x += f.vx * dt;
        f.y += f.vy * dt;
        f.vy += NINJA_GRAVITY * dt;
      });
      const field = this.ninjaGetField();
      const w = (field && field.clientWidth) || 900;
      const h = (field && field.clientHeight) || 520;
      // 移除落出屏幕的水果（阈值需大于跃出偏移，防止刚生成就被过滤）
      const alive = p.fruits.filter(
        (f) => f.y - f.size / 2 < h + NINJA_SPAWN_OFFSET + 60 && f.x > -f.size && f.x < w + f.size
      );
      if (alive.length !== p.fruits.length) {
        // 目标词水果落出屏幕没人切：直接判为错误（扣一颗心），进入下一题，不反复重抛
        const target = p.targetList[p.targetIndex];
        const missed = !p.completed && target && p.fruits.some((f) => !alive.includes(f) && f.english === target.english);
        if (missed) {
          p.lives--;
          p.combo = 0;
          p.wrong++;
          p.failFlash = true;
          setTimeout(() => {
            p.failFlash = false;
          }, 320);
          // 音效跟随音效设置弹窗中配置的"答错音效"
          this.playAnswerFeedback(false);
          if (p.lives <= 0) {
            this.ninjaGameResult = "lose";
            p.fruits = [];
            this.ninjaCompletePlayer(key);
            return;
          }
          p.targetIndex++;
          if (p.targetIndex >= p.targetList.length) {
            this.ninjaGameResult = "win";
            p.fruits = [];
            this.ninjaCompletePlayer(key);
            return;
          }
        }
        p.fruits = alive;
      }
      if (p.completed) {
        // 已完成（目标走完或生命耗尽）：等场上水果完全消失后再结算，弹出成绩
        if (!p.fruits.length) {
          this.endGame();
        }
        return;
      }
      // 本轮全部落幕后尽快抛出下一轮
      if (!p.fruits.length && now >= p.nextSpawnAt) {
        this.ninjaSpawnRound(key, w, h);
      }
    },

    /** 生成一轮水果：1 个目标词 + 3 个干扰词，槽位错开从底部抛出（互不重叠） */
    ninjaSpawnRound(key, w, h) {
      const p = this.ninjaData[key];
      const target = p.targetList[p.targetIndex];
      if (!target) return;
      const pool = (this.vocabulary || []).filter((v) => v.english !== target.english);
      const shuffledPool = [...pool].sort(() => 0.5 - Math.random());
      const words = [target];
      for (let i = 0; i < NINJA_ROUND_SIZE - 1 && i < shuffledPool.length; i++) {
        words.push(shuffledPool[i]);
      }
      const n = words.length;
      const slotW = w / n;
      // 槽位序号打乱后逐个分配，保证水平方向均匀错开
      const slots = [];
      for (let i = 0; i < n; i++) slots.push(i);
      const shuffledSlots = slots.sort(() => 0.5 - Math.random());
      p.fruits = words
        .map((word, i) => {
          // 尺寸不超过槽位宽度，从根源上避免相邻水果重叠（配合放大的字号）
          const size = Math.min(136 + Math.random() * 38, Math.max(108, slotW - 10));
          // 槽位中心 ± 小幅抖动（不超过槽位剩余空隙的 80%）
          const jitter = (Math.random() - 0.5) * Math.max(0, slotW - size) * 0.8;
          const x = (shuffledSlots[i] + 0.5) * slotW + jitter;
          // 抛物线顶点：先爬升 100px 进入屏幕，再在屏幕内上升 42%~72% 高度
          const peak = NINJA_SPAWN_OFFSET + h * (0.42 + Math.random() * 0.3);
          const vy = -Math.sqrt(2 * NINJA_GRAVITY * peak);
          // 水平速度朝屏幕中心偏置，放慢横移避免快速飞出
          const vx = (x < w / 2 ? 1 : -1) * (0.4 + Math.random() * 1.0);
          return {
            id: "f" + Math.random().toString(36).slice(2, 8),
            english: word.english,
            isTarget: word.english === target.english,
            colorIndex: Math.floor(Math.random() * NINJA_FRUIT_COLORS.length),
            size,
            x,
            y: h + NINJA_SPAWN_OFFSET, // 跃出位置：距底部 100px 左右
            vx,
            vy,
          };
        })
        .sort(() => 0.5 - Math.random());
    },

    /** 点击切割水果：判定目标词、计分、生命、连击（rot=刀光角度，滑动切割时为滑动方向） */
    handleNinjaSlice(key, fruit, event, rot) {
      const p = this.ninjaData[key];
      if (p.completed || this.gameState !== "playing") return;
      const target = p.targetList[p.targetIndex];
      const field = this.ninjaGetField();
      const rect = field && field.getBoundingClientRect();
      // 点击位置（相对游戏区，用于刀光特效）
      let cx = fruit.x;
      let cy = fruit.y;
      if (event && rect) {
        const pt = event.touches && event.touches[0] ? event.touches[0] : event;
        if (pt.clientX != null) {
          cx = pt.clientX - rect.left;
          cy = pt.clientY - rect.top;
        }
      }
      this.ninjaSpawnSlash(key, cx, cy, rot);
      if (target && fruit.english === target.english) {
        this.ninjaSliceCorrect(key, fruit, cx, cy);
      } else {
        this.ninjaSliceWrong(key, fruit, cx, cy);
      }
    },

    /**
     * 滑动切割：指针拖动划过水果即切割
     * 线段碰撞检测（上一采样点 → 当前点），快速甩动也不会漏切；
     * 单次采样事件只切一个水果，避免误伤相邻干扰词
     */
    handleNinjaSwipe(key, event) {
      const p = this.ninjaData[key];
      if (!p || p.completed || this.gameState !== "playing") return;
      const field = this.ninjaGetField();
      if (!field) return;
      const rect = field.getBoundingClientRect();
      const pt = event.touches && event.touches[0] ? event.touches[0] : event;
      if (pt.clientX == null) return;
      const x = pt.clientX - rect.left;
      const y = pt.clientY - rect.top;
      const last = p.swipeLast;
      p.swipeLast = { x, y };
      if (!last) return; // 首个采样点只记录位置，不判定
      for (const fruit of p.fruits) {
        const r = fruit.size / 2;
        if (ninjaSegmentDistance(fruit.x, fruit.y, last.x, last.y, x, y) <= r) {
          // 刀光角度 = 滑动方向（直线刀光与切割轨迹一致）
          const rot = (Math.atan2(y - last.y, x - last.x) * 180) / Math.PI;
          this.handleNinjaSlice(key, fruit, event, rot);
          return; // 单次采样只切一个
        }
      }
    },

    /** 滑动结束/开始：重置采样点（touchstart、touchend、mouseup 等时机调用） */
    ninjaSwipeReset(key) {
      const p = this.ninjaData[key];
      if (p) p.swipeLast = null;
    },

    /** 鼠标按下开始拖动切割（PC 端） */
    ninjaMouseDragStart(key) {
      this.ninjaMouseDragging = true;
      this.ninjaSwipeReset(key);
    },

    /** 鼠标拖动切割（PC 端）：仅在按住拖动时触发 */
    handleNinjaMouseDrag(key, event) {
      if (!this.ninjaMouseDragging) return;
      this.handleNinjaSwipe(key, event);
    },

    /** 鼠标结束拖动（PC 端） */
    ninjaMouseDragEnd(key) {
      this.ninjaMouseDragging = false;
      this.ninjaSwipeReset(key);
    },

    /** 切中目标词：百分制计分 + 连击特效 + 清场进入下一目标 */
    ninjaSliceCorrect(key, fruit, cx, cy) {
      const p = this.ninjaData[key];
      p.combo++;
      p.correct++;
      p.maxCombo = Math.max(p.maxCombo, p.combo);
      // 百分制：切中词数 = 纯正确数；总分 = 正确数 / 总题数 × 100（如 20 题对 5 题 = 25 分）
      this.player1Score = p.correct;
      this.player1Sum = Math.round((p.correct / p.targetList.length) * 100);
      this.ninjaSpawnParticles(key, cx, cy, fruit.colorIndex, true);
      // 音效跟随音效设置弹窗中配置的"答对音效"
      this.playAnswerFeedback(true);
      if (p.combo >= 3) {
        const comboVal = p.combo;
        p.comboFlash = comboVal;
        gameAudioManager.playNinjaCombo(comboVal);
        setTimeout(() => {
          if (p.comboFlash === comboVal) p.comboFlash = null;
        }, 1000);
      }
      // 切换到下一个目标词：最后一轮保留场上水果自然落完，其余清场进入下一轮
      p.targetIndex++;
      if (p.targetIndex >= p.targetList.length) {
        // 全部目标词切完：胜利
        this.ninjaGameResult = "win";
        this.ninjaCompletePlayer(key);
      } else {
        p.fruits = [];
        p.nextSpawnAt = performance.now() + 350;
      }
    },

    /** 切错：本题直接判错——扣一颗生命、连击清零、红闪，清场进入下一题（目标词落地不会再次扣心） */
    ninjaSliceWrong(key, fruit, cx, cy) {
      const p = this.ninjaData[key];
      p.lives--;
      p.combo = 0;
      p.wrong++;
      this.ninjaSpawnParticles(key, cx, cy, fruit.colorIndex, false);
      // 音效跟随音效设置弹窗中配置的"答错音效"
      this.playAnswerFeedback(false);
      p.failFlash = true;
      setTimeout(() => {
        p.failFlash = false;
      }, 320);
      if (p.lives <= 0) {
        // 生命耗尽：失败结算
        this.ninjaGameResult = "lose";
        this.ninjaCompletePlayer(key);
        return;
      }
      // 本题已判错：清空场上水果直接进入下一题（避免目标词落地时再次扣一颗生命）
      p.fruits = [];
      p.targetIndex++;
      if (p.targetIndex >= p.targetList.length) {
        this.ninjaGameResult = "win";
        this.ninjaCompletePlayer(key);
        return;
      }
      p.nextSpawnAt = performance.now() + 350;
    },

    /** 单人完成标记（目标词全部走完或生命耗尽）：直接清场并立即结算弹出弹窗 */
    ninjaCompletePlayer(key) {
      const p = this.ninjaData[key];
      p.completed = true;
      p.comboFlash = null;
      p.fruits = []; // 所有水果直接消失，不再等自然落完
      this.player1Completed = true;
      this.endGame();
    },

    /** 记录本局成绩到忍者榜（仅成绩与用时；按得分降序、同分用时短者靠前，最多 10 条） */
    ninjaRecordRank() {
      if (this.ninjaRankRecorded) return;
      this.ninjaRankRecorded = true;
      const time = this.ninjaStartTime ? Math.round((Date.now() - this.ninjaStartTime) / 1000) : 0;
      this.ninjaLastDuration = time;
      this.ninjaRankList.push({
        score: this.player1Sum, // 百分制总分（0-100）
        time,
      });
      this.ninjaRankList.sort((a, b) => b.score - a.score || a.time - b.time);
      if (this.ninjaRankList.length > 10) {
        this.ninjaRankList = this.ninjaRankList.slice(0, 10);
      }
      // 写入 sessionStorage：刷新页面后榜单仍在，关闭页面自动清空
      try {
        sessionStorage.setItem("ninja_rank_list", JSON.stringify(this.ninjaRankList));
      } catch (err) {
        /* 存储失败时仅保留内存数据 */
      }
      // 自动弹出本局成绩弹窗
      this.ninjaResultVisible = true;
    },

    /** 切割粒子爆裂：12 个彩点向四周飞散后自动清理 */
    ninjaSpawnParticles(key, cx, cy, colorIndex, success) {
      const p = this.ninjaData[key];
      const base = success ? NINJA_FRUIT_COLORS[colorIndex] : "#e74c3c";
      const particles = [];
      for (let i = 0; i < 12; i++) {
        const angle = (Math.PI * 2 * i) / 12 + Math.random() * 0.5;
        const dist = 45 + Math.random() * 55;
        const size = 6 + Math.random() * 7;
        particles.push({
          id: "pt" + Math.random().toString(36).slice(2, 8),
          x: cx,
          y: cy,
          size,
          dx: Math.round(Math.cos(angle) * dist),
          dy: Math.round(Math.sin(angle) * dist),
          color: Math.random() < 0.7 ? base : "#ffffff",
          flying: false,
        });
      }
      p.particles = p.particles.concat(particles);
      // 下一帧触发飞散（transition 驱动）
      setTimeout(() => {
        particles.forEach((pt) => (pt.flying = true));
      }, 30);
      setTimeout(() => {
        const ids = particles.map((pt) => pt.id);
        p.particles = p.particles.filter((pt) => !ids.includes(pt.id));
      }, 800);
    },

    /** 刀光特效：切割处沿切割方向的白色直线光带，快速伸展淡出 */
    ninjaSpawnSlash(key, cx, cy, rot) {
      const p = this.ninjaData[key];
      const slash = {
        id: "sl" + Math.random().toString(36).slice(2, 8),
        x: Math.round(cx),
        y: Math.round(cy),
        // 优先使用滑动方向（真实切割轨迹），点击切割时随机角度
        rot: rot != null && !Number.isNaN(rot) ? Math.round(rot) : Math.round(-35 + Math.random() * 70),
      };
      p.slashes = p.slashes.concat([slash]);
      setTimeout(() => {
        p.slashes = p.slashes.filter((s) => s.id !== slash.id);
      }, 400);
    },

    /** 停止 rAF 循环（保留数据，供结果页展示；endGame / 重开局时调用） */
    ninjaStopLoop() {
      this.ninjaRunning = false;
      this.ninjaLastTime = null;
      if (this.ninjaRafId) {
        cancelAnimationFrame(this.ninjaRafId);
        this.ninjaRafId = null;
      }
    },

    /** 停止循环并重置数据（resetGame / 组件销毁时调用；排行榜保留至页面关闭） */
    ninjaCleanup() {
      this.ninjaStopLoop();
      this.ninjaData = {
        player1: createNinjaPlayer(),
      };
    },
  },
  destroyed() {
    this.ninjaCleanup();
  },
};
